import { useEffect, useMemo, type ReactNode } from 'react'
import { crmFetch, dataOf } from '../chat/api'
import { prefetchNewest } from '../chat/prefetch'
import { fetchNewestBody } from '../chat/useThread'
import { pinStore, useLoadPins } from '../workspace/pinStore'
import { serverAccountPins, uploadBrowserPins } from './accountPins'
import { subscribeAccount, subscribeAccountHistory } from './accountSocket'
import { ChatsDepsProvider, type ChatsDeps } from './ChatsDeps'
import { notifyPerson } from './notify'

/** The CRM's storage-key part for a value (cache.js crmCacheSafePart). */
function safePart(value: string, fallback: string): string {
  return (value.trim() || fallback).replace(/[^a-zA-Z0-9_.:-]/g, '_')
}

/** The real Chats dependencies for one account: CRM-local routes with the console's token. */
export function AccountChatsDeps({ accountId, crmUserId, children }: { accountId: string; crmUserId: string; children: ReactNode }) {
  const storageScope = `${safePart(crmUserId, 'anon')}::${safePart(accountId, 'no-account')}`
  useLoadPins(crmUserId)
  // Pins this browser kept before they moved to the server (ADR 0035) go up once.
  useEffect(() => {
    if (!crmUserId) return
    // Straight to the gateway: Workspace wraps its chat in these deps without the library's.
    void uploadBrowserPins(pinStore, accountId, storageScope, async (ids) => dataOf(await crmFetch('/v1/users/list', { accountId, body: { user_ids: ids, param_name: 'm' } })))
  }, [crmUserId, accountId, storageScope])
  const deps = useMemo<ChatsDeps>(
    () => ({
      local: async <T,>(path: string, body: Record<string, unknown>, method: 'GET' | 'POST' | 'PUT' = 'POST') =>
        dataOf<T>(await crmFetch(path, { accountId, local: true, method, body: method === 'GET' ? undefined : body })),
      now: () => new Date(),
      storageScope,
      pins: serverAccountPins(pinStore, accountId),
      subscribe: subscribeAccount,
      history: subscribeAccountHistory,
      notify: (n) => void notifyPerson(n),
      prefetch: (fanId) => prefetchNewest(accountId, fanId, () => fetchNewestBody(accountId, fanId)),
    }),
    [accountId, storageScope],
  )
  return <ChatsDepsProvider value={deps}>{children}</ChatsDepsProvider>
}
